import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowUp, ArrowDown, CirclePlay, Database, Video, Pointer, PenTool, Newspaper, Volleyball, X, Code } from 'lucide-react';
import { projectsData } from '../data/projectsData';
import ProjectCard from '../components/ProjectCard';
import '../styles/Projects.css';

const Projects: React.FC = () => {
  const location = useLocation();
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [isScrolled, setIsScrolled] = useState(false);
  const heroRef = useRef<HTMLElement>(null);
  
  const categories = [
    { name: 'automation', icon: CirclePlay },
    { name: 'data', icon: Database },
    { name: 'ar', icon: Video },
    { name: 'touch', icon: Pointer },
    { name: 'branding', icon: PenTool },
    { name: 'news', icon: Newspaper },
    { name: 'sports', icon: Volleyball }
  ];
  
  const filteredProjects = activeFilters.length === 0 
    ? projectsData 
    : projectsData.filter(project => 
        activeFilters.some(filter => project.categories.includes(filter as any))
      );

  const sortedProjects = [...filteredProjects].sort((a, b) => {
    const yearA = parseInt(a.year || '0');
    const yearB = parseInt(b.year || '0');
    return sortOrder === 'desc' ? yearB - yearA : yearA - yearB;
  });

  const toggleSort = () => {
    setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
  };

  const getPageTitle = () => {
    if (activeFilters.length === 1) {
      const title = activeFilters[0] === 'ar' ? 'AR' : activeFilters[0].toUpperCase();
      return `${title} - Pedro Sugasti`;
    }
    return 'WORK - Pedro Sugasti';
  };

  const getPageDescription = () => {
    if (activeFilters.length === 1) {
      const category = activeFilters[0];
      const descriptions: Record<string, string> = {
        automation: 'Broadcast automation and real-time graphics work by Pedro Sugasti',
        data: 'Data visualization and analytics work for broadcast and digital platforms',
        touch: 'Interactive touchscreen applications and interfaces for live broadcasting',
        news: 'News graphics and production systems for major TV networks',
        sports: 'Sports broadcasting graphics and interactive analysis tools',
        ar: 'Augmented reality and virtual studio work for television',
        branding: 'TV branding and visual identity work for broadcasters'
      };
      return descriptions[category] || 'Broadcast and interactive media work by Pedro Sugasti';
    }
    return 'Portfolio of broadcast graphics, data visualization, and interactive media work by Pedro Sugasti';
  };

  const toggleFilter = (categoryName: string) => {
    setActiveFilters(prev => 
      prev.includes(categoryName)
        ? prev.filter(f => f !== categoryName)
        : [...prev, categoryName]
    );
  };

  useEffect(() => {
    // Parse URL parameters for initial category filter
    const params = new URLSearchParams(location.search);
    const categoryParam = params.get('category');
    if (categoryParam && categories.some(c => c.name === categoryParam)) {
      setActiveFilters([categoryParam]);
    }
    
    // Scroll to top when entering projects page (not when coming back from a project detail)
    // Check if we're coming from a project detail page by looking at document.referrer
    const referrer = document.referrer;
    const isComingFromProjectDetail = (referrer.includes('/work/') || referrer.includes('/projects/')) && referrer !== window.location.href;
    
    if (!isComingFromProjectDetail) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location.search]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      // Consider scrolled when user has scrolled more than 100px
      setIsScrolled(scrollPosition > 100);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <Helmet>
        <title>{getPageTitle()}</title>
        <meta name="description" content={getPageDescription()} />
        
        {/* Open Graph tags */}
        <meta property="og:title" content={getPageTitle()} />
        <meta property="og:description" content={getPageDescription()} />
        <meta property="og:image" content="https://pedrosugasti.com/images/projects-preview.jpg" />
        <meta property="og:url" content={`https://pedrosugasti.com/work${activeFilters.length > 0 ? `?category=${activeFilters[0]}` : ''}`} />
        <meta property="og:type" content="website" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={getPageTitle()} />
        <meta name="twitter:description" content={getPageDescription()} />
        <meta name="twitter:image" content="https://pedrosugasti.com/images/projects-preview.jpg" />
        
        <link rel="canonical" href={`https://pedrosugasti.com/work${activeFilters.length > 0 ? `?category=${activeFilters[0]}` : ''}`} />
      </Helmet>
      
      <div className="projects">
      <section ref={heroRef} className={`projects-hero ${isScrolled ? 'scrolled' : ''}`}>
        <div className="hero-content">
          <Code size={24} className="projects-hero-icon" />
          <div className="projects-hero-text">
            <h1>
              {activeFilters.length === 1 
                ? activeFilters[0] === 'ar' ? 'AR' : activeFilters[0].toUpperCase()
                : 'WORK'
              }
            </h1>
            <p className="lead">
              {activeFilters.length === 1
                ? (() => {
                    const subtitles: Record<string, string> = {
                      automation: 'Orchestrating broadcast workflows with intelligent systems',
                      data: 'Transforming numbers into compelling visual narratives',
                      ar: 'Blending virtual and reality for immersive storytelling',
                      touch: 'Interactive experiences at your fingertips',
                      branding: 'Crafting visual identities that resonate on-air',
                      news: 'Real-time graphics for breaking stories worldwide',
                      sports: 'Dynamic visualizations for the thrill of the game'
                    };
                    return subtitles[activeFilters[0]] || 'Showcase of recent work';
                  })()
                : activeFilters.length > 1 
                  ? `Mixing ${activeFilters.slice(0, -1).join(', ') + ' and ' + activeFilters[activeFilters.length - 1]} expertise`
                  : 'Broadcast graphics and interactive experiences'
              }
            </p>
          </div>
        </div>
      </section>

      <section className="projects-content">
        <div className="projects-controls">
          <div className="filter-buttons">
            {categories.map(category => {
              const Icon = category.icon;
              return (
                <button
                  key={category.name}
                  className={`filter-btn ${activeFilters.includes(category.name) ? 'active' : ''}`}
                  onClick={() => toggleFilter(category.name)}
                >
                  <div className="filter-icon-wrapper">
                    <Icon size={20} />
                    <div className="filter-tooltip-balloon">
                      {category.name.toUpperCase()}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          
          <div className="action-buttons">
            {activeFilters.length > 0 && (
              <button 
                className="clear-filters-btn"
                onClick={() => setActiveFilters([])}
              >
                <div className="clear-icon-wrapper">
                  <X size={20} />
                  <div className="filter-tooltip-balloon">
                    CLEAR FILTERS ({activeFilters.length})
                  </div>
                </div>
              </button>
            )}
            
            <button className="sort-btn" onClick={toggleSort}>
              <div className="sort-icon-wrapper">
                {sortOrder === 'desc' ? <ArrowDown size={20} /> : <ArrowUp size={20} />}
                <div className="filter-tooltip-balloon">
                  SORT BY YEAR
                </div>
              </div>
            </button>
          </div>
        </div>

        <div className="projects-grid">
          {sortedProjects.map(project => (
            <ProjectCard key={project.id} project={project} />
          ))}
        </div>

        <div className="projects-results">
          <p className="results-count">
            Showing {sortedProjects.length} of {projectsData.length} works
          </p>
        </div>
      </section>
      </div>
    </>
  );
};

export default Projects;